import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Check, Info } from "lucide-react";
import { toast } from "sonner";
import { ColorPicker } from "@/pages/ColorListPage";
import { useAuth } from "@/context/AuthContext";

export default function TagsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tags, setTags] = useState([]);
  const [activities, setActivities] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [color, setColor] = useState("#9333ea");
  const [activityIds, setActivityIds] = useState([]);

  const load = async () => {
    try {
      const [t, a] = await Promise.all([api.get("/tags"), api.get("/activities")]);
      setTags(t.data);
      setActivities(a.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); }, []);

  const activityMap = useMemo(
    () => Object.fromEntries(activities.map((a) => [a.id, a])),
    [activities]
  );

  const openCreate = () => {
    setEditing(null); setName(""); setColor("#9333ea"); setActivityIds([]); setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it); setName(it.name); setColor(it.color);
    setActivityIds(it.activity_ids || []); setOpen(true);
  };

  const toggleActivity = (id) => {
    setActivityIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const save = async () => {
    if (!name.trim()) return;
    try {
      const payload = { name: name.trim(), color, activity_ids: activityIds };
      if (editing) {
        await api.put(`/tags/${editing.id}`, payload);
        toast.success("Category updated");
      } else {
        await api.post("/tags", payload);
        toast.success("Category created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete category "${it.name}"? It will be removed from any time entries currently tagged.`)) return;
    try {
      await api.delete(`/tags/${it.id}`);
      load();
      toast.success("Category deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="flex-1 min-h-0 overflow-y-auto p-8" data-testid="tags-page">
      <div className="max-w-5xl mx-auto w-full">
      <div className="flex items-start justify-between gap-6 mb-6">
        <div className="pr-6 max-w-2xl">
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: "Manrope" }}>
            Categories
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Group activities under categories. When an activity has categories attached, those categories auto-apply to new time entries (you can always override).
          </p>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} className="text-white shrink-0" style={{ background: "#145c8c" }}
                      data-testid="tag-add-button">
                <Plus className="h-4 w-4 mr-1" /> New Category
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? "Edit Category" : "New Category"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1"
                       placeholder="e.g. EDM" data-testid="tag-name-input" />
              </div>
              <div>
                <Label>Colour</Label>
                <div className="mt-2">
                  <ColorPicker value={color} onChange={setColor} testId="tag-color-picker" />
                </div>
              </div>
              <div>
                <Label>Linked activities</Label>
                <p className="text-xs text-slate-500 mt-1">
                  This category will be auto-applied when a time entry uses any of these activities.
                </p>
                <div className="mt-2 max-h-48 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100"
                     data-testid="tag-activities-list">
                  {activities.length === 0 ? (
                    <div className="px-3 py-4 text-xs text-slate-400 text-center">
                      No activities yet — create one first.
                    </div>
                  ) : activities.map((a) => {
                    const selected = activityIds.includes(a.id);
                    return (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => toggleActivity(a.id)}
                        className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 ${
                          selected ? "bg-slate-50" : ""
                        }`}
                        data-testid={`tag-activity-toggle-${a.id}`}
                      >
                        <span className="h-3 w-3 rounded-full shrink-0" style={{ background: a.color }} />
                        <span className="flex-1">{a.name}</span>
                        {selected && <Check className="h-4 w-4 text-[#145c8c]" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="text-white" style={{ background: "#145c8c" }}
                      data-testid="tag-save-button">
                {editing ? "Save" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {!isAdmin && (
        <div className="mb-4 px-4 py-3 rounded-md bg-[#e8f1f8] border border-[#bcd6e6] text-sm text-[#0b2c47] flex items-start gap-2"
             data-testid="tag-readonly-banner">
          <Info className="h-4 w-4 mt-0.5 shrink-0 text-[#145c8c]" />
          <span>
            Read-only view. Contact your administrator to add, edit or remove categories.
          </span>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {tags.length === 0 ? (
          <div className="py-16 text-center text-slate-400 text-sm">No categories yet — create your first one above.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Colour</th>
                <th className="text-left px-4 py-3">Linked activities</th>
                {isAdmin && <th className="text-right px-4 py-3">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {tags.map((t) => (
                <tr key={t.id} className="border-t border-slate-100" data-testid={`tag-row-${t.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">
                    <span className="inline-flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold"
                            style={{ background: `${t.color}1a`, color: t.color }}>
                        {t.name}
                      </span>
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="h-4 w-4 rounded-full" style={{ background: t.color }} />
                      <span className="text-xs text-slate-500 timer-font">{t.color}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {(t.activity_ids || []).length === 0 ? (
                      <span className="text-xs text-slate-400">— none —</span>
                    ) : (
                      <div className="flex flex-wrap gap-1" data-testid={`tag-linked-${t.id}`}>
                        {(t.activity_ids || []).map((aid) => {
                          const a = activityMap[aid];
                          if (!a) return null;
                          return (
                            <span key={aid}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-700">
                              <span className="h-2 w-2 rounded-full" style={{ background: a.color }} />
                              {a.name}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(t)} className="p-1.5 hover:bg-slate-100 rounded-md text-slate-500"
                              data-testid={`tag-edit-${t.id}`}>
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => remove(t)} className="p-1.5 hover:bg-red-50 rounded-md text-red-500"
                              data-testid={`tag-delete-${t.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      </div>
    </div>
  );
}
